import type { ReportDiscreteBarChartData } from "@repo/app-types/entities/report";
import { SERIES_COLORS } from "@repo/ui/Charts/analyticsColors";
import { ChartSkeleton } from "@repo/ui/Skeletons/skeletons";
import { useMemo, type FC, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
  type LegendPayload,
  type TooltipContentProps,
} from "recharts";
import { BaseCard } from "./baseCard.js";
import { BaseTooltipContent } from "./baseTooltipContent.js";
import { renderValueAsString } from "./values.js";

export interface DiscreteBarChartCardProps {
  title: ReactNode;
  data?: ReportDiscreteBarChartData;
}

const HORIZONTAL_ROW_HEIGHT = 40;
const HORIZONTAL_MIN_HEIGHT = 220;

export const DiscreteBarChartCard: FC<DiscreteBarChartCardProps> = (props) => {
  const { data } = props;

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.categories.map(({ label, values }) => {
      const row: Record<string, string | number | null> = { label };
      data.series.forEach((series, index) => {
        row[series.label] = values[index] ?? null;
      });
      return row;
    });
  }, [data]);

  const hasData =
    data &&
    data.categories.length > 0 &&
    data.categories.some((c) => c.values.some((v) => v !== null));

  const hasLegend = data && data.series.length > 1;
  const isHorizontal = data?.orientation === "horizontal";

  const tickStyle = {
    fontSize: "0.75rem",
    fill: "var(--color-gray-500)",
  };
  const labelStyle = {
    fontSize: "0.75rem",
    fill: "var(--color-gray-500)",
  };

  const categoryAxis = data ? (
    isHorizontal ? (
      <YAxis
        type="category"
        dataKey="label"
        width="auto"
        stroke="none"
        interval={0}
        tick={tickStyle}
      >
        {data.yLabel ? (
          <Label
            value={data.yLabel}
            angle={-90}
            position="insideLeft"
            offset={-15}
            style={{ ...labelStyle, textAnchor: "middle" }}
          />
        ) : null}
      </YAxis>
    ) : (
      <XAxis dataKey="label" stroke="none" tick={tickStyle}>
        {data.xLabel ? (
          <Label
            value={data.xLabel}
            position="insideBottom"
            offset={-5}
            style={labelStyle}
          />
        ) : null}
      </XAxis>
    )
  ) : null;

  const valueAxis = data ? (
    isHorizontal ? (
      <XAxis
        type="number"
        stroke="none"
        tick={tickStyle}
        tickFormatter={(value) =>
          renderValueAsString({ value, valueType: data.yValueType })
        }
      >
        {data.xLabel ? (
          <Label
            value={data.xLabel}
            position="insideBottom"
            offset={-5}
            style={labelStyle}
          />
        ) : null}
      </XAxis>
    ) : (
      <YAxis
        type="number"
        width="auto"
        stroke="none"
        tick={tickStyle}
        tickFormatter={(value) =>
          renderValueAsString({ value, valueType: data.yValueType })
        }
      >
        {data.yLabel ? (
          <Label
            value={data.yLabel}
            angle={-90}
            position="insideLeft"
            offset={-15}
            style={{ ...labelStyle, textAnchor: "middle" }}
          />
        ) : null}
      </YAxis>
    )
  ) : null;

  const chartStyle = isHorizontal
    ? {
        width: "100%",
        height: Math.max(
          HORIZONTAL_MIN_HEIGHT,
          (data?.categories.length ?? 0) * HORIZONTAL_ROW_HEIGHT +
            (hasLegend ? 40 : 20) +
            (data?.xLabel ? 20 : 0),
        ),
      }
    : {
        width: "100%",
        aspectRatio: "2 / 1",
      };

  return (
    <BaseCard title={props.title}>
      {data ? (
        <>
          {!hasData ? (
            <div className="flex flex-auto items-center justify-center py-12 text-center text-sm text-gray-400">
              No data
            </div>
          ) : (
            <div className="-m-[5px] mt-2">
              <BarChart
                style={chartStyle}
                layout={isHorizontal ? "vertical" : "horizontal"}
                margin={{
                  top: 5,
                  right: 5,
                  bottom: data.xLabel ? 20 : 5,
                  left: data.yLabel ? 30 : 5,
                }}
                responsive
                data={chartData}
              >
                <CartesianGrid
                  stroke="var(--color-gray-200)"
                  strokeWidth={1}
                  vertical={isHorizontal}
                  horizontal={!isHorizontal}
                />
                {data.series.map((series, index) => (
                  <Bar
                    key={series.label}
                    dataKey={series.label}
                    fill={SERIES_COLORS[index % SERIES_COLORS.length]}
                    radius={4}
                  />
                ))}
                {categoryAxis}
                {valueAxis}
                {hasLegend ? (
                  <Legend
                    verticalAlign="bottom"
                    wrapperStyle={{ fontSize: "0.75rem" }}
                    content={RoundedSquareLegendContent}
                  />
                ) : null}
                <Tooltip
                  content={(tooltipProps) => (
                    <DiscreteBarChartTooltipContent
                      {...tooltipProps}
                      yValueType={data.yValueType}
                    />
                  )}
                />
              </BarChart>
            </div>
          )}
        </>
      ) : (
        <div style={{ aspectRatio: 1.618, maxHeight: "70vh" }}>
          <ChartSkeleton />
        </div>
      )}
    </BaseCard>
  );
};

const LEGEND_ICON_SIZE = 10;

const RoundedSquareLegendContent = ({
  payload,
}: {
  payload?: readonly LegendPayload[];
}) => {
  if (!payload?.length) return null;
  return (
    <ul className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
      {payload.map((entry, index) => (
        <li
          key={`${entry.value}-${index}`}
          className="inline-flex items-center gap-1.5"
        >
          <span
            className="inline-block rounded-[3px]"
            style={{
              width: LEGEND_ICON_SIZE,
              height: LEGEND_ICON_SIZE,
              backgroundColor: entry.color,
            }}
          />
          <span className="text-foreground">{entry.value}</span>
        </li>
      ))}
    </ul>
  );
};

const DiscreteBarChartTooltipContent: FC<
  TooltipContentProps & {
    yValueType: ReportDiscreteBarChartData["yValueType"];
  }
> = (props) => {
  if (!props.active || !props.payload?.length) {
    return null;
  }

  const label = props.payload[0]?.payload?.label as string | undefined;

  return (
    <BaseTooltipContent
      label={label ?? ""}
      entries={props.payload.map((entry) => ({
        color: entry.color,
        label: entry.name,
        value: renderValueAsString({
          value: (entry.value as number | null) ?? null,
          valueType: props.yValueType,
        }),
      }))}
    />
  );
};
